'use client';

import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import {Box, Stack} from '@nib/layout';
import HeaderFooterLayout from '@nib-components/header-footer-layout';
import Copy from '@nib-components/copy';
import Heading, {validSizeValues, sizeValues, componentValues, validComponentValues} from '@nib-components/heading';
import {ErrorTemplateImage} from './error-template-image';
import {ThemeContext} from 'styled-components';
import {SadComputerGraphicIcon} from '@nib/icons';
import {BreakpointType} from '@nib-components/theme';

const validVariationValues = ['arhi', 'shareholders', 'nz', 'foundation', 'thrive', 'providers', 'international', 'internationalStudents', 'internationalWorkers'] as const;
type variationValues = (typeof validVariationValues)[number];

type Nullable<T> = T | null;
type ResponsiveProp<T> = Partial<Record<BreakpointType, T>>;
type ResponsiveOrStaticProp<T> = T | ResponsiveProp<T>;

interface ErrorTemplateProps {
  title?: string;
  description?: string;
  children?: React.ReactNode;
  showSmallHeaderMenu?: boolean;
  isCorporate?: boolean;
  invertSmallHeader?: boolean;
  variation?: variationValues;
  smallLayout?: boolean;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  titleComponent?: componentValues;
  titleSize?: ResponsiveOrStaticProp<Nullable<sizeValues>>;
  [key: string]: unknown; // other props
}

const ErrorTemplate: React.FC<ErrorTemplateProps> = props => {
  const {title, description, children, titleComponent, titleSize = 1, ...otherProps} = props;
  const themeContext = useContext(ThemeContext);
  const nibBrands = ['nib', 'foundation'];

  const ErrorTemplateDisplayImage = nibBrands.includes(themeContext?.id) ? <ErrorTemplateImage title={title} /> : <SadComputerGraphicIcon size="xxxl" />;

  return (
    <HeaderFooterLayout data-mesh-component="ERROR-TEMPLATE" smallLayout flex {...otherProps}>
      <Box display="flex" alignItems="center" justifyContent="center" width="100%" paddingVertical={{xs: 5, sm: 8}} paddingHorizontal={5} background="warmWhite20" textAlign="center">
        <Stack space={6}>
          {ErrorTemplateDisplayImage}
          {title && (
            <Heading size={titleSize} component={titleComponent} color="trueGreen">
              {title}
            </Heading>
          )}
          {description && (
            <Copy size="large" align="center">
              {description}
            </Copy>
          )}
          <div>{children}</div>
        </Stack>
      </Box>
    </HeaderFooterLayout>
  );
};

ErrorTemplate.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  showSmallHeaderMenu: PropTypes.bool,
  invertSmallHeader: PropTypes.bool,
  variation: PropTypes.oneOf(validVariationValues),
  titleComponent: PropTypes.oneOf(validComponentValues),
  titleSize: PropTypes.oneOfType([
    PropTypes.oneOf(validSizeValues),
    PropTypes.shape({
      xs: PropTypes.oneOf(validSizeValues),
      sm: PropTypes.oneOf(validSizeValues),
      md: PropTypes.oneOf(validSizeValues),
      lg: PropTypes.oneOf(validSizeValues),
      xl: PropTypes.oneOf(validSizeValues),
      xxl: PropTypes.oneOf(validSizeValues),
      xxxl: PropTypes.oneOf(validSizeValues)
    })
  ]),
  smallLayout: PropTypes.bool,
  header: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  footer: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
};

ErrorTemplate.displayName = 'ErrorTemplate';

export default ErrorTemplate;
